import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { getBestAvailableUrl } from '@components/shared/OptimizedImage';

/**
 * ResourceSelector - displays a single image with checkbox
 * Shows thumbnail, dimensions, and file size
 */
function ResourceSelector({ imageId, file, isSelected, onSelect, onHover, onFocus, webpSizeInfo }) {
    const handleCheckboxChange = (e) => {
        onSelect(imageId, !isSelected);
    };
    
    const handleFocus = () => {
        if (onFocus && file) {
            onFocus(file, imageId);
        }
    };
    
    const handleBlur = () => {
        // Keep preview on blur - user might be navigating with keyboard
        // Preview will be updated when another tile is focused
    };
    
    // Get thumbnail URL (prefer smaller size for grid)
    // Uses getBestAvailableUrl which properly handles fallbacks based on actual file availability
    const thumbnailUrl = file ? getBestAvailableUrl(file, 480) : null;
    
    // Get webp file info for download (prefer 1920px, fallback to others)
    // Use the fetched webpSizeInfo if available, otherwise determine from file structure
    const getWebpFileInfo = () => {
        // If we have fetched size info, use it
        if (webpSizeInfo) {
            return {
                downloadSize: webpSizeInfo.downloadSize === 'original' ? 'original' : webpSizeInfo.downloadSize,
                size: webpSizeInfo.size
            };
        }
        
        // Fallback: determine which size is available (but we won't have the actual file size)
        const has1920 = !!(file?.url?.srcset?.['1920'] || file?.url?.srcset?.[1920]);
        const has1280 = !!(file?.url?.srcset?.['1280'] || file?.url?.srcset?.[1280]);
        const has768 = !!(file?.url?.srcset?.['768'] || file?.url?.srcset?.[768]);
        const has480 = !!(file?.url?.srcset?.['480'] || file?.url?.srcset?.[480]);
        
        const downloadSize = has1920 ? '1920px' : has1280 ? '1280px' : has768 ? '768px' : has480 ? '480px' : 'original';
        
        return {
            downloadSize: downloadSize,
            size: null // Can't determine without API call
        };
    };
    
    const webpInfo = getWebpFileInfo();
    
    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    };
    
    const description = file?.description;
    
    return (
        <div
            data-resource-tile
            data-image-id={imageId}
            className={cx(
                'border rounded-lg p-3 cursor-pointer transition-all bg-white relative',
                'hover:shadow-md hover:border-gray-400 focus-within:ring-2 focus-within:ring-blue-300',
                isSelected && 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
            )}
            onClick={handleCheckboxChange}
            onMouseEnter={() => onHover && onHover(file, imageId)}
            onMouseLeave={() => onHover && onHover(null, null)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={0}
            onKeyDown={(e) => {
                // Don't prevent default for arrow keys - let parent handle navigation
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCheckboxChange(e);
                }
            }}
        >
            {/* Checkbox positioned absolutely in top-left corner */}
            <input
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                tabIndex={-1}
                className="absolute top-2 left-2 w-4 h-4 cursor-pointer z-10"
            />
            <div className="min-w-0">
                {/* Thumbnail */}
                {thumbnailUrl ? (
                    <div className="mb-2 rounded overflow-hidden bg-gray-100 aspect-square flex items-center justify-center">
                        <img
                            src={`/${thumbnailUrl}`}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                const errorDiv = document.createElement('div');
                                errorDiv.className = 'text-xs opacity-50';
                                errorDiv.textContent = 'Failed to load';
                                e.target.parentElement.appendChild(errorDiv);
                            }}
                        />
                    </div>
                ) : (
                    <div className="mb-2 rounded bg-gray-200 aspect-square flex items-center justify-center text-xs opacity-50">
                        No image
                    </div>
                )}
                
                {/* Description */}
                {description && (
                    <div className="text-xs font-medium mb-1 line-clamp-2 opacity-90">
                        {description}
                    </div>
                )}
                
                {/* Metadata - WebP file info */}
                {webpInfo.downloadSize !== 'original' && (
                    <div className="text-xs opacity-75">
                        {webpInfo.downloadSize} WebP {webpInfo.size ? formatFileSize(webpInfo.size) : ''}
                    </div>
                )}
            </div>
        </div>
    );
}

ResourceSelector.propTypes = {
    imageId: PropTypes.string.isRequired,
    file: PropTypes.object.isRequired,
    isSelected: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
    onHover: PropTypes.func,
    onFocus: PropTypes.func,
    webpSizeInfo: PropTypes.shape({
        size: PropTypes.number,
        downloadSize: PropTypes.string,
    }),
};

export default ResourceSelector;
